import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Radio, DatePicker, Button, Select } from "antd";
import dayjs from "dayjs";
import { svg } from "../../../../Assets/svg";
import { dayjsFormat, isInvoiceFeatureEnabled } from "../../../../helpers/utils";
import { useDispatch } from "react-redux";
import { addQuotationMilestoneAPI } from "../quotationApis";
import { useAppDispatch } from "../../../../store";
import {
  getPaymentMilestoneStatusOptions,
  getQuoteStatusOptions,
  PaymentMilestoneStatus,
  PaymentMilestoneStatusLabels,
  QuoteStatus,
  QuoteStatusLabels,
} from "../quotationTypes";
import { OrderStatusEnum } from "../../Orders/orderTypes";

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (values: any) => void;
  milestoneData?: any;
  isInstantAddition?: boolean;
  quotationId?: string;
  existingMilestones?: any[];
  currencySymbol?: string;
}

export const AddPaymentMilestoneModal: React.FC<Props> = ({
  open,
  onClose,
  onSave,
  milestoneData,
  isInstantAddition = false,
  quotationId,
  existingMilestones = [],
  currencySymbol = "£",
}) => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  // Renamed state to match backend key
  const [valueBase, setValueBase] = React.useState<"percentage" | "amount">(
    "percentage",
  );

  useEffect(() => {
    if (open) {
      if (milestoneData) {
        // --- EDIT MODE ---
        const currentBase = milestoneData.value_base || "percentage"; // ✅ Use unified key
        setValueBase(currentBase);

        form.setFieldsValue({
          // ✅ Map directly to backend keys
          milestone_number: milestoneData.milestone_number?.toString(),
          milestone_name: milestoneData.milestone_name,
          value_base: currentBase,

          // ✅ Logic to populate the correct visual input from the single 'value' key
          paymentPercent:
            currentBase === "percentage" ? milestoneData.value : null,
          paymentAmount: currentBase === "amount" ? milestoneData.value : null,

          action: milestoneData.action || null,
        });
      } else {
        // --- ADD MODE ---
        form.resetFields();
        let defaultBase: "percentage" | "amount" = "percentage";

        const hasPercentage = existingMilestones?.some(
          (m: any) => m.value_base === "percentage",
        );
        const hasAmount = existingMilestones?.some(
          (m: any) => m.value_base === "amount",
        );

        if (hasAmount && !hasPercentage) {
          defaultBase = "amount";
        }

        setValueBase(defaultBase);
        form.setFieldsValue({ value_base: defaultBase });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, milestoneData, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const finalValue =
        values.value_base === "amount"
          ? Number(values.paymentAmount)
          : Number(values.paymentPercent);

      const formattedValues: any = {
        _id: milestoneData?._id, // Preserve ID

        // ✅ Return Standardized Keys
        milestone_number: Number(values.milestone_number),
        milestone_name: values.milestone_name,
        value_base: values.value_base,
        value: finalValue, // Unified value

        action: values.action,
      };
      if (isInstantAddition) {
        setSaving(true);
        formattedValues.quote_id = quotationId;
        formattedValues.payment_milestone_id = milestoneData?._id;
        dispatch(
          addQuotationMilestoneAPI({
            payload: formattedValues,
            onSuccess: (data) => {
              onSave(data);
              setSaving(false);
              onClose();
            },
            onError: () => {
              setSaving(false);
            },
          }),
        );
      } else {
        onSave(formattedValues);
      }
    } catch (error) {
      console.log("Validation Failed:", error);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      title={
        <div className="text-center fs-4 fw-bolder lh-m">
          {milestoneData ? "Update Payment Milestone" : "Add Payment Milestone"}
        </div>
      }
      closeIcon={svg?.closeIcon}
      width={"33.75rem"}
      className="commonModalClass cmc-select"
    >
      <Form
        layout="vertical"
        form={form}
        initialValues={{ value_base: "percentage", action: null }}
      >
        <Form.Item
          name="milestone_number" // ✅ Updated Name
          label={<div className="custom-label mb-0 ff">Milestone Number</div>}
          rules={[
            { required: true, message: "Enter Milestone Number" },
            { whitespace: true, message: "Required" },
          ]}
          required={false}
        >
          <Input
            className="form-control form-control-lg"
            size="large"
            placeholder="Enter Milestone Number"
            type="number" // ✅ Added type number
            onWheel={(e: any) => e.currentTarget.blur()}
          />
        </Form.Item>

        <Form.Item
          name="milestone_name" // ✅ Updated Name
          label={<div className="custom-label mb-0 ff">Milestone Name</div>}
          rules={[
            { required: true, message: "Enter Milestone Name" },
            { whitespace: true, message: "Required" },
          ]}
          required={false}
        >
          <Input
            className="form-control form-control-lg"
            size="large"
            placeholder="Enter Milestone Name"
          />
        </Form.Item>

        <Form.Item
          name="value_base" // ✅ Updated Name
          label={<div className="custom-label mb-0 ff">Milestone Based On</div>}
          className="antCol_mb-0"
        >
          <Radio.Group
            value={valueBase}
            onChange={(e) => setValueBase(e.target.value)}
            className="w-100"
          >
            <div className="row">
              <div className="col-6">
                <Radio
                  value="percentage"
                  className="fs-6 text-primary customRadioButton"
                  disabled={existingMilestones?.some(
                    (m: any) => m.value_base === "amount",
                  )}
                >
                  Percentage
                </Radio>
              </div>
              <div className="col-6">
                <Radio
                  value="amount"
                  className="fs-6 text-primary customRadioButton"
                  disabled={existingMilestones?.some(
                    (m: any) => m.value_base === "percentage",
                  )}
                >
                  Amount
                </Radio>
              </div>
            </div>
          </Radio.Group>
        </Form.Item>

        <div className="row">
          <div className="col-sm-6">
            {/* We keep different names here for UI purposes, but map them to 'value' on save */}
            {valueBase === "percentage" && (
              <Form.Item
                name="paymentPercent"
                label={<div className="custom-label mb-0 ff">Payment %</div>}
                normalize={(value) =>
                  value ? value.replace(/[^0-9.]/g, "") : value
                }
                rules={[
                  { required: true, message: "Enter %" },
                  { pattern: /^[0-9.]+$/, message: "Invalid number" },
                  {
                    validator: async (_, value) => {
                      if (value && Number(value) > 100) {
                        return Promise.reject(new Error("Cannot exceed 100%"));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                required={false}
              >
                <Input
                  className="form-control form-control-lg"
                  size="large"
                  placeholder="0"
                  prefix={<span className="text-white">%</span>}
                />
              </Form.Item>
            )}

            {valueBase === "amount" && (
              <Form.Item
                name="paymentAmount"
                label={
                  <div className="custom-label mb-0 ff">Payment Amount</div>
                }
                normalize={(value) =>
                  value ? value.replace(/[^0-9.]/g, "") : value
                }
                rules={[
                  { required: true, message: "Enter Amount" },
                  { pattern: /^[0-9.]+$/, message: "Invalid amount" },
                ]}
                required={false}
              >
                <Input
                  className="form-control form-control-lg amountInput"
                  size="large"
                  placeholder="0"
                  prefix={<span className="text-white">{currencySymbol}</span>}
                />
              </Form.Item>
            )}
          </div>

          {isInvoiceFeatureEnabled && (
            <div className="col-sm-6">
              <Form.Item
                name="action"
                label={<div className="custom-label mb-0 ff">Action</div>}
                rules={[{ required: true, message: "Select Action" }]}
                required={false}
              >
                <Select
                  className="ant-select-single"
                  placeholder="Select Action"
                  options={[
                    {
                      label: QuoteStatusLabels[QuoteStatus.APPROVED],
                      value: QuoteStatus.APPROVED,
                    },
                    {
                      label: "Ready For Dispatch",
                      value: OrderStatusEnum.READY_FOR_DISPATCH,
                    },
                    {
                      label: "Delivered",
                      value: OrderStatusEnum.DELIVERED,
                    },
                    {
                      label: "Installed",
                      value: OrderStatusEnum.INSTALLED,
                    },
                    {
                      label: "Invoiced",
                      value: OrderStatusEnum.CLOSED,
                    },
                  ]}
                />
              </Form.Item>
            </div>
          )}
        </div>

        <div className="d-flex justify-content-center gap-3">
          <Button
            className="btn btn-lg btn-outline-secondary h-auto px-5"
            onClick={onClose}
            disabled={saving}
          >
            CLOSE
          </Button>
          <Button
            className="btn btn-lg btn-primary h-auto px-5"
            type="primary"
            onClick={handleSave}
            loading={saving}
            disabled={saving}
          >
            {milestoneData ? `UPDATE` : `SAVE`}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};
